/**
 * GTM Analytics Service
 *
 * Pushes events to window.dataLayer for Google Tag Manager (server-side via stape.io).
 * Server container URL and GTM Container ID are env-configurable via VITE_SGTM_URL / VITE_GTM_CONTAINER_ID.
 *
 * All "first" events check backend flags before firing — skip silently if already triggered.
 * Guards are internal — callers do not need their own checks.
 *
 * After firing a first event, the in-memory Zustand store is updated immediately to prevent
 * re-fire within the same session. Callers must also persist the flag to the backend via
 * `usePersistFirstEventFlag()` to prevent re-fire across sessions.
 */

import { useMutation } from '@apollo/client/react';
import { UPDATE_USER_SETTINGS_AUTH } from '@scio3/graphql-client';
import type { UpdateUserSettingsInput } from '@scio3/shared-types';
import { useAuthStore } from '@/stores/auth-store';

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
    __OPTI_TEST_MODE?: boolean;
  }
}

/**
 * SECURITY-CRITICAL: prod hostname allowlist for the test-mode rollback guard.
 *
 * The `is_test` field is silently dropped from dataLayer pushes when the page
 * is loaded from any hostname matching this regex — even if `window.__OPTI_TEST_MODE`
 * is set. This is the only mechanism preventing accidental test-traffic markers
 * from leaking onto real users (devtools, browser extensions, leaked env vars).
 *
 * Update in lockstep with any change to prod hostnames. Unit-tested in
 * analytics.test.ts.
 */
export const PROD_HOSTS = /^(app|www)\.optisigns\.com$/;

function getUser() {
  const { me } = useAuthStore.getState();
  if (!me) return null;
  return {
    userId: me._id,
    user_email: me.email,
    user_role: me.rootRole,
    accountId: me.accountId,
    userSettings: me.userSettings,
    appMetas: me.appMetas,
  };
}

/** Lookup appCategories from me.appMetas by appType, join as comma-separated string (matches scio1). */
function getAppCategory(
  appMetas: { appType?: string; appCategories?: string[] }[] | undefined,
  appType: string | undefined,
): string {
  if (!appType || !appMetas) return '';
  const meta = appMetas.find((m) => m.appType === appType);
  return (meta?.appCategories ?? []).join(',');
}

function pushEvent(event: string, extra?: Record<string, unknown>) {
  const user = getUser();
  if (!user) return;

  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isProdHost = PROD_HOSTS.test(hostname);
  const isTestMode = !isProdHost && window.__OPTI_TEST_MODE === true;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event,
    userId: user.userId,
    user_email: user.user_email,
    accountId: user.accountId,
    ...(isTestMode ? { is_test: true } : {}),
    ...extra,
  });
}

/** Update in-memory userSettings flag to prevent re-fire within the same session. */
function markFlagInStore(flagName: string) {
  const { me } = useAuthStore.getState();
  if (!me) return;
  useAuthStore.setState({
    me: {
      ...me,
      userSettings: {
        ...(me.userSettings as Record<string, unknown>),
        [flagName]: true,
      },
    },
  });
}

// ── Public API ──────────────────────────────────────────

/** Fires on every login (fresh + session resume). */
export function trackLogin() {
  pushEvent('loginEvent');
}

/**
 * Conversion event — tracked in GTM / Google Ads as a conversion.
 * Fires once per account — checks company.payment.firstDevicePaired (auto-set by backend).
 */
export function trackFirstDevicePaired() {
  const { company } = useAuthStore.getState();
  if (!company) return; // company not loaded yet — skip to avoid false positive
  if (company.payment?.firstDevicePaired) return;
  pushEvent('firstDevicePaired');
}

/**
 * Conversion event — tracked in GTM / Google Ads as a conversion.
 * Fires once per account when a trial starts. Mirrors scio1
 * `trackingTrialCreated` (analytics-service.js:160) — payload is just
 * `{event, userId, user_email, accountId}` (those are added by
 * `pushEvent` automatically). Caller is responsible for the
 * "fire-once" guard via `company.payment.trialStartEventFired`.
 */
export function trackTrialCreated() {
  pushEvent('trialCreated');
}

/**
 * Fires when a winback (OptiReach) 30-day trial claim succeeds. Deliberately
 * DISTINCT from `trialCreated` — the backend leaves `trialStartEventFired`
 * untouched on reactivation, so the trial-created conversion tag must NOT
 * re-fire. Best-effort: `pushEvent` no-ops until `me` is in the auth store
 * (the login-flow claim runs before user context loads).
 */
export function trackWinbackReactivated(params: { campaign: string }) {
  pushEvent('winbackReactivated', { campaign: params.campaign });
}

/** Fires once per user — checks userSettings.firstAssetCreated. Returns true if event was pushed. */
export function trackFirstAssetCreated(params: {
  type: string;
  fileType?: string;
  appType?: string;
}): boolean {
  const user = getUser();
  if (!user) return false;
  if (user.userSettings?.firstAssetCreated) return false;

  pushEvent('firstAssetCreated', {
    user_role: user.user_role,
    assetType: params.type,
    type: params.type,
    fileType: params.fileType ?? '',
    appType: params.appType ?? '',
    appCategory: getAppCategory(user.appMetas, params.appType),
  });
  markFlagInStore('firstAssetCreated');
  return true;
}

/** Fires on every asset create (not just first). */
export function trackNewAssetCreated(params: {
  type: string;
  fileType?: string;
  appType?: string;
}) {
  const user = getUser();
  if (!user) return;

  pushEvent('newAssetCreated', {
    user_role: user.user_role,
    assetType: params.type,
    type: params.type,
    fileType: params.fileType ?? '',
    appType: params.appType ?? '',
    appCategory: getAppCategory(user.appMetas, params.appType),
  });
}

/** Fires once per user — checks userSettings.firstPlaylistCreated. Returns true if event was pushed. */
export function trackFirstPlaylistCreated(params?: {
  itemCount?: number;
  totalDuration?: number;
}): boolean {
  const user = getUser();
  if (!user) return false;
  if (user.userSettings?.firstPlaylistCreated) return false;

  pushEvent('firstPlaylistCreated', {
    user_role: user.user_role,
    itemCount: params?.itemCount ?? 0,
    total_duration: params?.totalDuration ?? 0,
  });
  markFlagInStore('firstPlaylistCreated');
  return true;
}

/** Fires on every playlist create (not just first). */
export function trackNewPlaylistCreated(params?: { itemCount?: number; totalDuration?: number }) {
  const user = getUser();
  if (!user) return;

  pushEvent('newPlaylistCreated', {
    user_role: user.user_role,
    itemCount: params?.itemCount ?? 0,
    total_duration: params?.totalDuration ?? 0,
  });
}

/** Fires once per user — checks userSettings.firstScheduleCreated. Returns true if event was pushed. */
export function trackFirstScheduleCreated(): boolean {
  const user = getUser();
  if (!user) return false;
  if (user.userSettings?.firstScheduleCreated) return false;

  pushEvent('firstScheduleCreated', {
    user_role: user.user_role,
  });
  markFlagInStore('firstScheduleCreated');
  return true;
}

/** Fires on every schedule create (not just first). */
export function trackNewScheduleCreated() {
  const user = getUser();
  if (!user) return;

  pushEvent('newScheduleCreated', {
    user_role: user.user_role,
  });
}

// ── Team Member Invited (Phase 5) ───────────────────────

interface TeamMemberInvitedParams {
  /** Optional — scio1 GTM payload doesn't include the invitee's email, but adding it
   *  here keeps the API symmetric with other event factories. Excluded from the
   *  pushed payload (kept GTM-compatible). */
  email?: string;
}

/** Fires once per user — checks userSettings.firstTeamMemberInvited.
 *  Returns true if event was pushed. Mirrors scio1 firstTeamMemberInvited
 *  (analytics-service.js:497). */
export function trackFirstTeamMemberInvited(_params?: TeamMemberInvitedParams): boolean {
  const user = getUser();
  if (!user) return false;
  if (user.userSettings?.firstTeamMemberInvited) return false;

  pushEvent('firstTeamMemberInvited', {
    user_role: user.user_role,
  });
  markFlagInStore('firstTeamMemberInvited');
  return true;
}

/** Fires on every team-member invite (not just first). Mirrors scio1
 *  newTeamMemberInvited (analytics-service.js:529). */
export function trackNewTeamMemberInvited(_params?: TeamMemberInvitedParams) {
  const user = getUser();
  if (!user) return;

  pushEvent('newTeamMemberInvited', {
    user_role: user.user_role,
  });
}

// ── Template Used (Phase 6) ─────────────────────────────

/** Fires once per user — checks userSettings.firstTemplateUsed. Returns true
 *  if event was pushed. Mirrors scio1 firstTemplateUsed (analytics-service.js:569). */
export function trackFirstTemplateUsed(templateId: string): boolean {
  const user = getUser();
  if (!user || !templateId) return false;
  if (user.userSettings?.firstTemplateUsed) return false;

  pushEvent('firstTemplateUsed', {
    user_role: user.user_role,
    templateId,
  });
  markFlagInStore('firstTemplateUsed');
  return true;
}

/** Fires on every template apply (not just first). Mirrors scio1
 *  newTemplateUsed (analytics-service.js:602). */
export function trackNewTemplateUsed(templateId: string) {
  const user = getUser();
  if (!user || !templateId) return;

  pushEvent('newTemplateUsed', {
    user_role: user.user_role,
    templateId,
  });
}

// ── Device Ordered (Phase 7) ────────────────────────────

interface DeviceOrderedParams {
  productName?: string;
  productPrice?: number;
  quantity?: number;
  totalAmount?: number;
}

/** Fires once per user — checks userSettings.firstDeviceOrdered. Returns true
 *  if event was pushed. Mirrors scio1 firstDeviceOrdered (analytics-service.js:644). */
export function trackFirstDeviceOrdered(params: DeviceOrderedParams): boolean {
  const user = getUser();
  if (!user) return false;
  if (user.userSettings?.firstDeviceOrdered) return false;

  pushEvent('firstDeviceOrdered', {
    user_role: user.user_role,
    productName: params.productName ?? '',
    productPrice: params.productPrice ?? 0,
    quantity: params.quantity ?? 0,
    totalAmount: params.totalAmount ?? 0,
  });
  markFlagInStore('firstDeviceOrdered');
  return true;
}

/** Fires on every device order (not just first). Mirrors scio1
 *  newDeviceOrdered (analytics-service.js:678). */
export function trackNewDeviceOrdered(params: DeviceOrderedParams) {
  const user = getUser();
  if (!user) return;

  pushEvent('newDeviceOrdered', {
    user_role: user.user_role,
    productName: params.productName ?? '',
    productPrice: params.productPrice ?? 0,
    quantity: params.quantity ?? 0,
    totalAmount: params.totalAmount ?? 0,
  });
}

// ── Subscription (GTM revenue events) ───────────────────
// Both `firstSubscription` and `subUpdate` are conversion events
// — tracked in GTM / Google Ads as conversions.

interface SubscriptionEventParams {
  planId: string;
  /** Total screens for firstSubscription; DELTA (new - current) for subUpdate. */
  noOfDevices: number;
  /** ISO currency code (e.g. 'USD'). */
  currency: string;
  /** 'monthly' | 'yearly' — scio1 wire format, lowercase. */
  stripeDuration: string;
  /** Unit price per screen. */
  stripePlanPrice: number;
  stripeDiscount?: number;
}

/** Conversion event. Fires on the FIRST paid subscription. scio1 parity: trackingFirstInitialSubscriptionTracking. */
export function trackFirstSubscription(params: SubscriptionEventParams) {
  pushEvent('firstSubscription', {
    noOfDevices: params.noOfDevices,
    stripeCurrency: params.currency,
    stripeDiscount: params.stripeDiscount ?? 0,
    stripeDuration: params.stripeDuration,
    stripePlanPrice: params.stripePlanPrice,
    planID: params.planId,
    // scio3 inherits scio1's "1 = new-generation UI" semantic
    scio20: 1,
  });
}

/** Conversion event. Fires on subsequent subscription changes. scio1 parity: trackingSubUpdate. */
export function trackSubUpdate(params: SubscriptionEventParams) {
  pushEvent('subUpdate', {
    noOfDevices: params.noOfDevices,
    stripeCurrency: params.currency,
    stripeDiscount: params.stripeDiscount ?? 0,
    stripeDuration: params.stripeDuration,
    stripePlanPrice: params.stripePlanPrice,
    planID: params.planId,
    scio20: 1,
  });
}

// ── Persistence Hook ────────────────────────────────────

type FirstEventFlag =
  | 'firstAssetCreated'
  | 'firstPlaylistCreated'
  | 'firstScheduleCreated'
  | 'firstTeamMemberInvited'
  | 'firstTemplateUsed'
  | 'firstDeviceOrdered';

/**
 * Hook that returns a function to persist a first-event flag to the backend.
 * Call after a `trackFirst*()` function returns `true`.
 */
export function usePersistFirstEventFlag() {
  const [mutate] = useMutation(UPDATE_USER_SETTINGS_AUTH);
  return (flag: FirstEventFlag) => {
    const updateData: UpdateUserSettingsInput = { [flag]: true };
    mutate({ variables: { updateData } }).catch(() => {
      // Silently fail — in-memory guard still prevents re-fire within session.
      // The flag will be retried on the next "first" event if the user creates again.
    });
  };
}
